// IVR Documenter API client.
//
// Endpoints (all under REACT_APP_IVR_API_BASE_URL):
//   POST /api/search                      → search use cases
//   POST /api/export                      → export filtered use cases as CSV blob
//   GET  /api/use-cases?<qs>              → paginated use-case list
//   GET  /api/stats                       → aggregate stats
//   GET  /api/admin/status                → ingestion status snapshot
//   GET  /api/admin/ingestion-log         → per-run history
//   GET  /api/admin/ingestion-progress    → live progress while a run is active
//
// Usage from a React component:
//   const { getApiToken } = useAuth();
//   const { results, total } = await ivrSearch({ query, filters, page }, getApiToken);

const API_BASE = process.env.REACT_APP_IVR_API_BASE_URL || '';

export function isIvrBackendConfigured() {
  return Boolean(API_BASE);
}

async function authHeaders(tokenProvider, withJsonContentType = false) {
  const headers = withJsonContentType ? { 'Content-Type': 'application/json' } : {};
  if (tokenProvider) {
    try {
      const token = await tokenProvider();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      console.warn('ivr: token acquisition failed', err);
    }
  }
  return headers;
}

async function parseOrThrow(res, fallbackPrefix) {
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { detail: text };
  }
  if (!res.ok) {
    const err = new Error(data?.detail || data?.message || `${fallbackPrefix} failed (HTTP ${res.status})`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

function ensureConfigured() {
  if (!API_BASE) {
    throw new Error('IVR API URL is not configured. Set REACT_APP_IVR_API_BASE_URL.');
  }
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

const SEARCH_DEFAULTS = {
  n_results: 200,
  search_type: 'use_cases',
  max_distance: 1.2,
  page_size: 40
};

function buildSearchBody({ query = '', filters = {}, page = 1, pageSize = SEARCH_DEFAULTS.page_size }) {
  const {
    vertical = null,
    platform = null,
    complexity = null,
    self_service_only = null,
    year_min = null,
    year_max = null
  } = filters;
  return {
    query,
    filters: { vertical, platform, complexity, self_service_only, year_min, year_max },
    n_results: SEARCH_DEFAULTS.n_results,
    search_type: SEARCH_DEFAULTS.search_type,
    max_distance: SEARCH_DEFAULTS.max_distance,
    page,
    page_size: pageSize
  };
}

export async function ivrSearch({ query, filters, page, pageSize } = {}, tokenProvider) {
  ensureConfigured();
  const headers = await authHeaders(tokenProvider, true);
  const res = await fetch(`${API_BASE}/api/search`, {
    method: 'POST',
    headers,
    body: JSON.stringify(buildSearchBody({ query, filters, page, pageSize }))
  });
  return parseOrThrow(res, 'Search');
}

/**
 * Trigger a server-side CSV export of all use cases matching `filters`.
 * Returns a Blob the caller is responsible for turning into a download.
 */
export async function ivrExportCsv({ filters = {} } = {}, tokenProvider) {
  ensureConfigured();
  const headers = await authHeaders(tokenProvider, true);
  const res = await fetch(`${API_BASE}/api/export`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      format: 'csv',
      filters: {
        vertical: filters.vertical ?? null,
        platform: filters.platform ?? null,
        complexity: filters.complexity ?? null,
        self_service_only: filters.self_service_only ?? null,
        year_min: filters.year_min ?? null,
        year_max: filters.year_max ?? null
      }
    })
  });
  if (!res.ok) {
    const err = new Error(`Export failed (HTTP ${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.blob();
}

// ---------------------------------------------------------------------------
// Browser (use-case listing)
// ---------------------------------------------------------------------------

/**
 * List use cases with optional filters.
 *
 * queryParams: object whose values may be a scalar (set as a single param) or
 * an array (appended once per element — e.g. `complexity: ['S','M']` becomes
 * `complexity=S&complexity=M`). null / undefined / '' values are dropped.
 */
export async function ivrListUseCases(queryParams = {}, tokenProvider) {
  ensureConfigured();
  const headers = await authHeaders(tokenProvider);
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(queryParams)) {
    if (v === null || v === undefined || v === '') continue;
    if (Array.isArray(v)) v.forEach((item) => qs.append(k, String(item)));
    else qs.set(k, String(v));
  }
  const url = qs.toString() ? `${API_BASE}/api/use-cases?${qs}` : `${API_BASE}/api/use-cases`;
  const res = await fetch(url, { headers });
  return parseOrThrow(res, 'List use cases');
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export async function ivrStats(tokenProvider) {
  ensureConfigured();
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/api/stats`, { headers });
  return parseOrThrow(res, 'Load stats');
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export async function ivrAdminStatus(tokenProvider) {
  ensureConfigured();
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/api/admin/status`, { headers });
  return parseOrThrow(res, 'Load admin status');
}

export async function ivrIngestionLog(tokenProvider) {
  ensureConfigured();
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/api/admin/ingestion-log`, { headers });
  return parseOrThrow(res, 'Load ingestion log');
}

export async function ivrIngestionProgress(tokenProvider) {
  ensureConfigured();
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/api/admin/ingestion-progress`, { headers });
  return parseOrThrow(res, 'Load ingestion progress');
}
